import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Clock, Target, TrendingUp, RefreshCw, CheckCircle2, Sparkles, CalendarClock, XCircle } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { sportIcon, intensityClass } from "../lib/workout";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import CompleteFlow from "../components/CompleteFlow";
import AdjustFlow from "../components/AdjustFlow";
import WorkoutCharts from "../components/WorkoutCharts";
import PlannedView from "../components/PlannedView";

function todayStr() { return new Date().toISOString().slice(0, 10); }

export default function WorkoutDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [w, setW] = useState(null);
  const [detail, setDetail] = useState(null);
  const [flowOpen, setFlowOpen] = useState(false);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [garmin, setGarmin] = useState(false);
  const [skipping, setSkipping] = useState(false);

  const load = () => api.get(`/workout/${id}`).then((r) => setW(r.data));
  useEffect(() => { load(); api.get("/garmin/status").then((r) => setGarmin(!!r.data.connected)).catch(() => {}); }, [id]);

  // load device detail for completed+linked workouts
  useEffect(() => {
    if (w?.status === "completed" && w?.garmin_external_id) {
      api.get(`/activity/${w.garmin_external_id}/detail`).then((r) => setDetail(r.data.activity?.detail)).catch(() => {});
    }
  }, [w?.status, w?.garmin_external_id]);

  const onDone = (data) => { setW(data.workout); };

  const skip = async () => {
    setSkipping(true);
    try {
      const { data } = await api.post(`/workout/${id}/complete`, { completed: false, note: null });
      setW(data.workout);
      toast("Marked as not done");
    } catch (e) {
      toast(e.response?.data?.detail || "Couldn't update");
    } finally { setSkipping(false); }
  };

  if (!w) return <div className="space-y-4"><Skeleton className="h-8 w-32" /><Skeleton className="h-96 w-full rounded-2xl" /></div>;

  const Icon = sportIcon(w.sport);
  const isFuture = w.date > todayStr();
  const isToday = w.date === todayStr();
  const canLog = w.status === "planned" && !w.is_rest && !isFuture;

  return (
    <div className="space-y-6">
      <button onClick={() => navigate(-1)} data-testid="workout-back" className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft size={16} /> Back
      </button>

      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="rounded-2xl border border-border bg-card overflow-hidden">
        {w.adapted && (
          <div className="flex items-center gap-1.5 text-xs font-semibold bg-brand-orange/15 text-brand-orange px-6 py-2.5" data-testid="workout-adapted">
            <RefreshCw size={13} /> Caden adapted this from your original plan
          </div>
        )}
        <div className="p-6 sm:p-8">
          <div className="flex items-center justify-between mb-6">
            <span className={`inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full ${intensityClass(w.intensity)}`}>
              {w.intensity}
            </span>
            <div className="flex items-center gap-1.5 text-muted-foreground text-sm font-medium"><Clock size={15} /> {w.duration_min} min</div>
          </div>

          <div className="flex items-start gap-4 mb-6">
            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 ${w.is_rest ? "bg-muted" : "brand-gradient"}`}>
              <Icon className={w.is_rest ? "text-muted-foreground" : "text-white"} size={26} />
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{w.sport} · {w.type}</p>
              <h1 className="font-heading font-black text-3xl tracking-tight text-foreground mt-0.5" data-testid="workout-title">{w.title}</h1>
            </div>
          </div>

          <div className="space-y-5">
            <Info icon={Target} label="Purpose" text={w.purpose} color="text-brand-orange" />
            <Info icon={TrendingUp} label="Why it helps" text={w.benefit} color="text-brand-yellow" />
            {!w.is_rest && w.segments?.length > 0 && <PlannedView segments={w.segments} />}
            {w.details && (
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">The session</p>
                <div className="bg-muted/60 rounded-xl p-4 text-foreground leading-relaxed whitespace-pre-line" data-testid="workout-details">{w.details}</div>
              </div>
            )}
            {w.original && (
              <div className="text-sm text-muted-foreground border-t border-border pt-4">
                Originally planned: <span className="text-foreground font-medium">{w.original.title}</span> ({w.original.duration_min} min). Adjusted to fit today.
              </div>
            )}
          </div>

          {/* action zone */}
          {canLog && (
            <div className="flex flex-col sm:flex-row gap-3 mt-8">
              <Button data-testid="workout-complete-btn" onClick={() => setFlowOpen(true)} className="flex-1 h-12 rounded-xl font-semibold active:scale-95 transition-transform">
                Complete this session
              </Button>
              {isToday ? (
                <Button data-testid="workout-adjust-btn" variant="outline" onClick={() => setAdjustOpen(true)} className="flex-1 h-12 rounded-xl font-semibold active:scale-95 transition-transform">
                  Adjust / can't do this
                </Button>
              ) : (
                <Button data-testid="workout-skip-btn" variant="outline" onClick={skip} disabled={skipping} className="flex-1 h-12 rounded-xl font-semibold active:scale-95 transition-transform">
                  Didn't do it
                </Button>
              )}
            </div>
          )}
          {w.status === "planned" && !w.is_rest && isFuture && (
            <div className="mt-8">
              <Button data-testid="workout-adjust-btn" variant="outline" onClick={() => setAdjustOpen(true)} className="h-11 rounded-xl font-semibold">
                Adjust this session
              </Button>
            </div>
          )}
          {w.status === "planned" && w.is_rest && (
            <div className="flex items-center gap-2 mt-8 text-muted-foreground font-medium" data-testid="workout-rest-note">
              <CheckCircle2 size={18} /> Rest day — nothing to log. Recovery is the work today.
            </div>
          )}
          {w.status === "planned" && !w.is_rest && isFuture && (
            <div className="flex items-center gap-2 mt-8 text-muted-foreground font-medium" data-testid="workout-future-note">
              <CalendarClock size={18} /> This session is scheduled for {w.date}. You can log it on the day.
            </div>
          )}
          {w.status !== "planned" && (
            <div className={`flex items-center gap-2 mt-8 font-semibold ${w.status === "completed" ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground"}`} data-testid="workout-done-status">
              {w.status === "completed" ? <CheckCircle2 size={20} /> : <XCircle size={20} />}
              {w.status === "completed" ? "Completed" : "Not done"}
            </div>
          )}
        </div>
      </motion.div>

      {/* Caden's performance analysis */}
      {w.analysis && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="rounded-2xl border border-border bg-card p-6" data-testid="workout-analysis">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="text-brand-orange" size={16} />
            <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Caden's analysis</p>
            {w.analysis.hit_intent && (
              <span className={`ml-auto text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${w.analysis.hit_intent === "yes" ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400" : w.analysis.hit_intent === "partial" ? "bg-brand-yellow/15 text-brand-yellow" : "bg-brand-orange/15 text-brand-orange"}`}>
                {w.analysis.hit_intent === "yes" ? "Nailed it" : w.analysis.hit_intent === "partial" ? "Partial" : "Off target"}
              </span>
            )}
          </div>
          <p className="text-foreground leading-relaxed text-[15px]" data-testid="analysis-summary">{w.analysis.summary}</p>
          {w.analysis.observations?.length > 0 && (
            <ul className="mt-4 space-y-1.5" data-testid="analysis-observations">
              {w.analysis.observations.map((o, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground"><span className="text-brand-orange mt-1.5 w-1 h-1 rounded-full bg-brand-orange shrink-0" /> {o}</li>
              ))}
            </ul>
          )}
        </motion.div>
      )}

      {/* Graphs / splits for completed device-verified sessions */}
      {w.status === "completed" && (w.actual || detail) && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="rounded-2xl border border-border bg-card p-6">
          <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-4">Your session data</p>
          <WorkoutCharts actual={w.actual} detail={detail} />
        </motion.div>
      )}

      <CompleteFlow workoutId={id} open={flowOpen} onOpenChange={setFlowOpen} garminConnected={garmin} onDone={onDone} />
      <AdjustFlow workoutId={id} open={adjustOpen} onOpenChange={setAdjustOpen} onDone={onDone} />
    </div>
  );
}

function Info({ icon: Icon, label, text, color }) {
  if (!text) return null;
  return (
    <div>
      <div className="flex items-center gap-2 mb-1.5">
        <Icon size={15} className={color} />
        <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{label}</p>
      </div>
      <p className="text-foreground leading-relaxed">{text}</p>
    </div>
  );
}
